import React from 'react';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  withMeta,
  withSystem,
  ElementPreset,
  Plugins,
  Atom,
} from '@packages/systems/wfdl';
import {memoize} from '@packages/utilities/memo';
import {px, percent, vh} from '@packages/systems/core/utils/WebflowCssSubset';
import {createRgbaColor} from '@packages/systems/core/utils/ColorUtils';
import {buildOrderConfirmation} from './CommerceUtils';
import {getPaddedUserItemsAndTotals, UserItem} from './utils/OrderItemsUtils';
import {
  SHARED_DYN_WRAPPER_CONSTRAINTS_ANCESTORS,
  SHARED_DYN_WRAPPER_CONSTRAINTS_DESCENDANTS,
} from '@packages/systems/core/constants/SharedConstraintRules';
import withAttribute from './withAttribute';
import {
  DATA_ATTR_NODE_TYPE,
  NODE_TYPE_COMMERCE_ORDER_CONFIRMATION_WRAPPER,
  ORDER_TYPE,
  ORDER_QUERY,
  PREVIEW_ITEMS_DEFAULT,
  PREVIEW_ITEMS_KEY,
} from '@packages/systems/commerce/constants';
import {WebflowEnvironments} from '@packages/systems/core-constants';

import {
  TListOfElement,
  TRecord,
  TText,
  TEnum,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment, useSite} from '@packages/systems/render';
import {
  EvaluatedNodeData,
  getChildren,
  getInPojo,
  getNumberOrElse,
  getStringOrElse,
} from '@packages/utilities/data-utils';
import {
  CurrencySettings,
  FormattedPrice,
} from '@packages/systems/core/utils/CurrencyUtils';

const CHECKOUT_BINDING_ROOT_QUERY_PATH = ['database', 'commerceOrder'];

const setRequiresShipping = (value: boolean) => (contextData: any) => ({
  ...contextData,
  requiresShipping: value,
});

const memoizedGetUserItemsAndTotals = memoize(
  (
    previewItemsCount: number,
    userItems: UserItem[],
    extraItems: {price: FormattedPrice}[],
    currencySettings: CurrencySettings
  ) =>
    getPaddedUserItemsAndTotals(
      previewItemsCount,
      userItems,
      extraItems,
      currencySettings
    )
);

const CommerceOrderConfirmationContainer = Atom.createRender(
  ({attributes, classes, context, data, generateAttributes, renderChild}) => {
    const site = useSite();
    const runtimeEnvironment = useRuntimeEnvironment();

    // Slight hack to overwrite mock order requiresShipping context to use toggle on form.
    const isDesigner = runtimeEnvironment === WebflowEnvironments.DESIGNER;
    const mockRequiresShipping =
      getStringOrElse(
        getInPojo(data, ['temp', 'requiresShipping']),
        ORDER_TYPE.REQUIRE_SHIPPING
      ) === ORDER_TYPE.REQUIRE_SHIPPING;

    const previewItemsCount = getNumberOrElse(
      getInPojo(data, ['temp', PREVIEW_ITEMS_KEY]),
      PREVIEW_ITEMS_DEFAULT
    );

    // TODO (ecommerce): Memoize commerce context mutations
    const newContext = context
      .update('data', (contextData) => {
        if (isDesigner) {
          const dataWithRequiresShipping = setRequiresShipping(
            mockRequiresShipping
          )(contextData || {});

          const currencySettings = context.get('ecommCurrencySettings');

          if (currencySettings && contextData) {
            const extraItems = contextData.extraItems || [];
            const userItems = contextData.userItems || [];
            const {paddedUserItems, subtotal, total} =
              memoizedGetUserItemsAndTotals(
                previewItemsCount,
                userItems,
                extraItems,
                currencySettings
              );
            return {
              ...dataWithRequiresShipping,
              userItems: paddedUserItems,
              subtotal,
              total,
            };
          }
          return dataWithRequiresShipping;
        }
        return contextData;
      })
      .set('bindingPathPrefix', CHECKOUT_BINDING_ROOT_QUERY_PATH)
      .set(
        'previewItemsCount',
        getNumberOrElse(
          getInPojo(data, ['temp', PREVIEW_ITEMS_KEY]),
          PREVIEW_ITEMS_DEFAULT
        )
      );

    const query = context.get('query') || '';

    const newAttributes = attributes
      .set(ORDER_QUERY, query)
      .set('data-wf-page-link-href-prefix', site.get('pageLinkHrefPrefix', ''));

    return (
      <div {...generateAttributes(newAttributes, classes)}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, newContext)
        )}
      </div>
    );
  }
);

export const createCommerceOrderConfirmationContainer: ElementPreset.ExpressionEscapeHatchFactory =
  ({collectionState, betaFeatures}) => {
    if (collectionState === undefined) {
      throw Error(
        'collectionState is required to create CommerceOrderConfirmationContainer'
      );
    }

    return buildOrderConfirmation({
      collectionState,
      betaFeatures: betaFeatures || null,
    });
  };

const config = createIntegratedAtom({
  displayName: 'Order Confirmation',
  showEmpty: true,
  constraints: {
    ancestors: SHARED_DYN_WRAPPER_CONSTRAINTS_ANCESTORS,
    descendants: SHARED_DYN_WRAPPER_CONSTRAINTS_DESCENDANTS,
  },
  style: {
    base: {
      main: {
        noPseudo: {
          width: percent(100),
          minHeight: vh(100),
          paddingTop: px(20),
          paddingBottom: px(20),
          paddingRight: px(20),
          paddingLeft: px(20),
          backgroundColor: createRgbaColor('f5f5f5'),
        },
      },
    },
  },
  render: withAttribute(
    DATA_ATTR_NODE_TYPE,
    NODE_TYPE_COMMERCE_ORDER_CONFIRMATION_WRAPPER
  )(CommerceOrderConfirmationContainer),
  fields: {
    commerce: withSystem(
      {private: true},
      TRecord({
        type: TText,
      })
    ),
    temp: TRecord({
      requiresShipping: withSystem(
        {
          label: 'Order',
          defaultValue: ORDER_TYPE.REQUIRE_SHIPPING,
        },
        TEnum({
          [ORDER_TYPE.REQUIRE_SHIPPING]: 'Shipping',
          [ORDER_TYPE.NO_SHIPPING]: 'No Shipping',
        })
      ),
      previewItems: withSystem(
        {
          label: 'Preview',
          defaultValue: PREVIEW_ITEMS_DEFAULT,
        },
        TTypeConstructor(qualify(Plugins.name('Basic'), typeName('Slider')))
      ),
      note: withMeta(
        {
          content:
            'You can preview up to 20 order items. If you do not have enough products, sample items will be generated.',
        },
        withSystem(
          {defaultValue: false},
          TTypeConstructor(
            qualify(Plugins.name('Commerce'), typeName('DismissibleNote'))
          )
        )
      ),
    }),
    children: TListOfElement,
  },
});

export default config;
